<script setup>
import {ref, computed, watch, defineProps, defineEmits, getCurrentInstance} from 'vue'
import { InvoiceStatus, InvoiceType, RecipientType } from '../types.js'
import Button from './ui/Button.vue'
import Checkbox from './ui/Checkbox.vue'
import Badge from './ui/Badge.vue'
import Dropdown from './ui/Dropdown.vue'
import PaymentLinkModal from './PaymentLinkModal.vue'
import {
	FileTextIcon,
	DownloadIcon,
	PlusIcon,
	FilterIcon,
	SearchIcon,
	PieChartIcon,
	InvoiceFilterLoaderIcon,
	LinkIcon,
	MoreHorizontalIcon,
	EditIcon,
	CopyIcon,
	BanIcon,
	TrashIcon,
	ArrowUpIcon,
	ArrowDownIcon
} from './Icons.js'
import ExportModal from './ExportModal.vue'
import { useInvoiceExport } from '../composables/useInvoiceExport'
import FilterPanel from './FilterPanel.vue'
import { XIcon } from './Icons'
import { useClickOutside } from '@/vue/composables/useClickOutside'
import { getInvoiceTypeLabel } from '../constants.js'
import { notify } from "@/utils/notify.js";
import { getLastDaysRange } from "@/utils/date.js";


const props = defineProps({
	invoices: {
		type: Array,
		default: () => [],
	},
	invoiceStats: {
		type: Array,
		default: () => [],
	},
})

const emit = defineEmits([
	'create',
	'edit',
	'duplicate',
	'cancel',
	'delete',
	'bulkCancel',
	'view',
	'export',
	'dateChanged'
])

const instance = getCurrentInstance()
const t = (key) => instance?.ctx.$i18n.t(key)

const { exportInvoices } = useInvoiceExport()

const { dateFrom, dateTo } = getLastDaysRange(2, { format: 'date' })

const initialFilterState = {
	dateFrom,
	dateTo,
	statuses: [],
	types: [],
	amountMin: '',
	amountMax: '',
	clientName: '',
	phone: '',
}

const filterState = ref({ ...initialFilterState })

const quickSearch = ref('')
const isFilterPanelOpen = ref(false)
const filterWrapper = ref(null)
const isStatusPanelOpen = ref(false)
const isExportModalOpen = ref(false)
const isPaymentModalOpen = ref(false)
const selectedInvoiceForPayment = ref(null)

const selectedIds = ref(new Set())

const sortConfig = ref(null)

const currentPage = ref(1)
const itemsPerPage = ref(10)
const isLoading = ref(false)

const statusOptions = Object.values(InvoiceStatus)

const filteredInvoices = ref(props.invoices.slice())
const stableSearch = ref('')
let filterTimer = null

const isSorted = (key) => sortConfig.value?.key === key
const sortDirection = (key) => {
	if (!isSorted(key)) return null
	return sortConfig.value.direction
}

const isDraft = (inv) => inv.status === InvoiceStatus.DRAFT
const isCanceled = (inv) => inv.status === InvoiceStatus.CANCELED
const isActivaved = (inv) => inv.status === InvoiceStatus.ACTIVATED;

const canEdit = (inv) => isDraft(inv)

const canCancel = (inv) => {
	if (!inv) return false

	if (isDraft(inv) || isActivaved(inv)) return true

	return false
}

const canDelete = (inv) => isDraft(inv) || isCanceled(inv)

const canDuplicate = () => true

const getRecipientContact = (inv) => {
	if (!inv?.recipient) return ''

	if (inv.recipient.type === RecipientType.INDIVIDUAL) {
		const p = inv.recipient.person || {}

		const contacts = []

		if (p.phone) contacts.push(p.phone)
		if (p.email) contacts.push(p.email)

		return contacts.join(' / ')
	}

	if (inv.recipient.type === RecipientType.LEGAL) {
		return inv.recipient.legal?.EDRPOU || ''
	}

	return ''
}

const clearFilter = (key, val) => {
	if (Array.isArray(filterState.value[key])) {
		if (val !== undefined) {
			filterState.value[key] = filterState.value[key].filter(v => v !== val)
		} else {
			filterState.value[key] = []
		}
	} else {
		filterState.value[key] = ''
	}

	currentPage.value = 1
}

watch(
	[() => props.invoices, filterState, quickSearch],
	() => {
		isLoading.value = true

		if (filterTimer) clearTimeout(filterTimer)

		filterTimer = setTimeout(() => {
			const src = props.invoices || []

			const searchLower = (stableSearch.value || '').trim().toLowerCase()
			const digits = searchLower.replace(/\D/g, '')
			const isPhoneQuery = digits.length >= 5 && !/[a-zа-яіїє@]/i.test(searchLower)
			const phoneSearch = isPhoneQuery ? digits : ''

			filteredInvoices.value = src.filter((inv) => {
				if (!inv) return false

				const recipientType = inv.recipient?.type
				const person = inv.recipient?.person || {}
				const legal = inv.recipient?.legal || {}

				const recipientName =
					recipientType === RecipientType.INDIVIDUAL
						? `${person.firstName || ''} ${person.lastName || ''}`.trim()
						: legal.name || ''

				const number = (inv.number || '').toLowerCase()
				const recLower = recipientName.toLowerCase()

				const email = (person.email || '').trim().toLowerCase()

				const phone = (person.phone || '').replace(/\D/g, '')
				const edrpou = (legal.EDRPOU || '').toLowerCase()

				const matchesQuickSearch =
					!searchLower ||
					number.includes(searchLower) ||
					recLower.includes(searchLower) ||
					email.includes(searchLower) ||
					(isPhoneQuery && phone.includes(phoneSearch)) ||
					edrpou.includes(searchLower)

				if (!matchesQuickSearch) return false

				if (filterState.value.dateTo) {
					const to = new Date(filterState.value.dateTo)
					to.setHours(23, 59, 59, 999)
					const invDate = new Date(inv.createdAt)
					if (invDate > to) return false
				}

				if (filterState.value.statuses.length > 0 && !filterState.value.statuses.includes(inv.status)) {
					return false
				}

				if (filterState.value.types.length > 0 && !filterState.value.types.includes(inv.type)) {
					return false
				}

				const minUAH = Number(filterState.value.amountMin)
				const maxUAH = Number(filterState.value.amountMax)
				const invAmountKop = Number(inv.totalAmount ?? 0)

				if (!Number.isNaN(minUAH) && filterState.value.amountMin !== '' && invAmountKop < minUAH) return false
				if (!Number.isNaN(maxUAH) && filterState.value.amountMax !== '' && invAmountKop > maxUAH) return false

				if (filterState.value.clientName) {
					const clientLower = filterState.value.clientName.toLowerCase()
					if (!recLower.includes(clientLower)) return false
				}

				if (filterState.value.phone) {
					const raw = (filterState.value.phone || '').trim().toLowerCase()

					if (raw.includes('@')) {
						const email = (person.email || '').trim().toLowerCase()
						if (!email.includes(raw)) return false
					} else {
						const digits = raw.replace(/\D/g, '')

						if (digits.length < 5) return false

						const invPhoneClean = (person.phone || '').replace(/\D/g, '')
						if (!invPhoneClean.includes(digits)) return false
					}
				}

				if (inv?.bill_id === 94) {
					console.log('[INV 94 EMAIL]', (inv?.recipient?.person?.email || '').trim().toLowerCase())
					console.log('[INV 94 MATCH]', matchesQuickSearch)
				}

				return true
			})

			isLoading.value = false
			currentPage.value = 1
		}, 400)
	},
	{ deep: true }
)

watch(quickSearch, (val) => {
	const v = (val ?? '').trim()
	if (v.length > 0) stableSearch.value = v
	else stableSearch.value = ''
})

watch(
	() => [filterState.value.dateFrom, filterState.value.dateTo],
	([newFrom, newTo], [oldFrom, oldTo]) => {
		if (newFrom !== oldFrom || newTo !== oldTo) {
			emit('dateChanged', {
				dateFrom: newFrom,
				dateTo: newTo,
			})
		}
	}
)

const getSortableValue = (inv, key) => {
	switch (key) {
		case 'recipientName':
			return getRecipientName(inv)?.toLowerCase() || ''

		case 'recipientContact':
			return getRecipientContact(inv)?.toLowerCase() || ''

		case 'status':
			return inv.status || ''

		case 'totalAmount':
			return Number(inv.totalAmount ?? 0)

		case 'date':
			return inv.date || ''

		case 'number':
			return inv.number || ''

		default:
			return inv?.[key] ?? ''
	}
}

const sortedInvoices = computed(() => {
	if (!sortConfig.value) return filteredInvoices.value

	const { key, direction } = sortConfig.value

	return [...filteredInvoices.value].sort((a, b) => {
		const aVal = getSortableValue(a, key)
		const bVal = getSortableValue(b, key)

		if (aVal < bVal) return direction === 'asc' ? -1 : 1
		if (aVal > bVal) return direction === 'asc' ? 1 : -1
		return 0
	})
})

const toolbarCount = computed(() => sortedInvoices.value.length)

const toolbarSum = computed(() => {
	return (sortedInvoices.value || []).reduce((sum, inv) => {
		const val = Number(inv?.totalAmount ?? 0)
		return sum + (Number.isFinite(val) ? val : 0)
	}, 0)
})

const toolbarSumFormatted = computed(() => {
	return toolbarSum.value.toLocaleString('en-US', { minimumFractionDigits: 2 })
})

const paginatedInvoices = computed(() => {
	const start = (currentPage.value - 1) * itemsPerPage.value
	return sortedInvoices.value.slice(start, start + itemsPerPage.value)
})

const totalPages = computed(() =>
	sortedInvoices.value.length === 0
		? 1
		: Math.ceil(sortedInvoices.value.length / itemsPerPage.value)
)

const activeFilterCount = computed(() => {
	let count = 0

	if (filterState.value.dateFrom || filterState.value.dateTo) count++
	if (filterState.value.amountMin || filterState.value.amountMax) count++
	if (filterState.value.statuses.length > 0) count++
	if (filterState.value.types.length > 0) count++
	if (filterState.value.clientName) count++
	if (filterState.value.phone) count++

	return count
})

const statusCounts = computed(() => {
	const counts = { all: props.invoices.length || 0 }
	statusOptions.forEach((s) => {
		counts[s] = 0
	})
	;(props.invoices || []).forEach((inv) => {
		if (!inv || !inv.status) return
		if (counts[inv.status] == null) counts[inv.status] = 0
		counts[inv.status]++
	})
	return counts
})

const handleExport = ({ scope, format, columns }) => {
	exportInvoices({
		scope,
		format,
		columns,
		invoices: props.invoices,
		filteredInvoices: filteredInvoices.value,
		selectedIds: selectedIds.value,
	})

	isExportModalOpen.value = false
}

const setStatusFilter = (status) => {
	if (status === 'all') {
		filterState.value.statuses = []
	} else {
		filterState.value.statuses = [status]
	}

	currentPage.value = 1
}

const getRecipientName = (inv) => {
	if (!inv || !inv.recipient) return ''

	if (inv.recipient.type === RecipientType.INDIVIDUAL) {
		const p = inv.recipient.person || {}
		return `${p.firstName || ''} ${p.lastName || ''}`.trim()
	}

	return (inv.recipient.legal && inv.recipient.legal.name) || t('invoices.on_payment')
}

const getStatusVariant = (status) => {
	switch (status) {
		case InvoiceStatus.FULLY_PAID:
		case InvoiceStatus.ACTIVATED:
			return 'success'
		case InvoiceStatus.PARTIALLY_PAID:
			return 'warning'
		case InvoiceStatus.ACTIVATED:
			return 'default'
		case InvoiceStatus.DRAFT:
			return 'secondary'
		case InvoiceStatus.CANCELED:
			return 'destructive'
		default:
			return 'outline'
	}
}

// selection helpers
const toggleSelection = (id) => {
	if (!id) return
	const set = selectedIds.value
	if (set.has(id)) set.delete(id)
	else set.add(id)
}

const toggleAll = () => {
	const all = filteredInvoices.value || []
	if (selectedIds.value.size === all.length && all.length > 0) {
		selectedIds.value = new Set()
	} else {
		const set = new Set()
		all.forEach((i) => {
			if (i && i.id) set.add(i.id)
		})
		selectedIds.value = set
	}
}

const selectedIdsArray = computed(() => Array.from(selectedIds.value))

const hasSelection = computed(() => selectedIds.value.size > 0)
const hasCancelableSelection = computed(() => bulkCancelableIds.value.length > 0)

const selectedInvoices = computed(() => {
	const set = selectedIds.value
	return (filteredInvoices.value || []).filter(inv => inv?.id && set.has(inv.id))
})

const bulkCancelableIds = computed(() => {
	return selectedInvoices.value
		.filter(inv => canCancel(inv))
		.map(inv => inv.id)
})

const bulkNotCancelableCount = computed(() => {
	return selectedInvoices.value.filter(inv => !canCancel(inv)).length
})

const clearSelection = () => {
	selectedIds.value = new Set()
}

const handleBulkExportSelected = () => {
	if (!hasSelection.value) return

	isExportModalOpen.value = true
}

const handleSort = (key) => {
	if (sortConfig.value && sortConfig.value.key === key) {
		sortConfig.value = {
			key,
			direction: sortConfig.value.direction === 'asc' ? 'desc' : 'asc',
		}
	} else {
		sortConfig.value = { key, direction: 'asc' }
	}
}

const handleBulkCancelClick = () => {
	if (selectedIds.value.size === 0) return

	const cancelIds = bulkCancelableIds.value
	const skipped = bulkNotCancelableCount.value
	console.log('bulkNotCancelableCount.value', bulkNotCancelableCount.value);

	if (cancelIds.length === 0) {
		notify.info(
			t('invoices.bulk_cancel_only_draft_or_activated') ||
			'Bulk cancel is available only for Draft or Activated invoices.'
		)
		return
	}

	if (skipped > 0) {
		notify.info(
			t('invoices.bulk_cancel_skipped')
		)
	}

	emit('bulkCancel', cancelIds)

	selectedIds.value = new Set()
}

const openPaymentModal = (inv) => {
	selectedInvoiceForPayment.value = inv
	isPaymentModalOpen.value = true
}

useClickOutside(filterWrapper, () => {
	isFilterPanelOpen.value = false
}, { closeOnEsc: true })
</script>

<template>
	<div class="flex flex-col min-h-screen bg-slate-50/50 p-6 space-y-4 relative mt-10 overflow-y-auto">
		<!-- Header Area -->
		<div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
			<h1 class="text-2xl font-bold text-slate-900 flex items-center gap-2">
				<div class="w-8 h-8 rounded slate flex items-center justify-center text-slate-600">
					<FileTextIcon class="w-5 h-5" />
				</div>
				{{ $t('invoices.invoices') }}
			</h1>
			<div class="flex items-center gap-2">
				<Button variant="outline" class="gap-2" @click="isExportModalOpen = true">
					<DownloadIcon class="w-4 h-4" />
					{{ $t('invoices.export') }}
				</Button>
				<Button @click="emit('create')" class="gap-2 shadow-sm">
					<PlusIcon class="w-4 h-4" />
					{{ $t('invoices.new_invoice') }}
				</Button>
			</div>
		</div>

		<div class="flex flex-col relative z-10 space-y-3">
			<div class="flex items-center gap-3">
				<div class="relative" ref="filterWrapper">
					<button
						@click="isFilterPanelOpen = !isFilterPanelOpen"
						:class="`flex items-center gap-2 px-4 py-2.5 rounded-lg border text-sm font-medium transition-all ${
              isFilterPanelOpen
                ? 'bg-slate-600 text-white bg-slate-600'
                : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
            }`"
					>
						<FilterIcon class="w-4 h-4" />
						{{ $t('invoices.filters') }}
						<span
							v-if="activeFilterCount > 0"
							class="bg-slate-600 text-white text-[10px] w-5 h-5 flex items-center justify-center rounded-full ml-1 font-bold"
						>
              {{ activeFilterCount }}
            </span>
					</button>

					<FilterPanel
						:isOpen="isFilterPanelOpen"
						:filterState="filterState"
						@close="isFilterPanelOpen = false"
						@apply="val => filterState = val"
						@reset="filterState = { ...initialFilterState }"
					/>

				</div>

				<button
					@click="isStatusPanelOpen = !isStatusPanelOpen"
					:class="[
						'flex items-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-all',
						isStatusPanelOpen
							? 'bg-slate-600 text-white bg-slate-600 shadow-md ring-2 ring-slate-600/20'
							: 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50 hover:border-slate-300'
					]"
				>
					<PieChartIcon class="w-4 h-4" />
				</button>

				<!-- Search -->
				<div class="relative flex-1 max-w-md">
					<div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
						<SearchIcon class="h-4 w-4 text-slate-400" />
					</div>
					<input
						v-model.trim="quickSearch"
						class="block w-full pl-10 pr-4 py-2.5 text-sm bg-white border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-500/20 focus:border-slate-500 transition-all placeholder:text-slate-400 shadow-sm"
						:placeholder="$t('invoices.search_placeholder')"
					/>
				</div>
			</div>

			<div
				v-if="isStatusPanelOpen"
				class="flex flex-wrap gap-2 pb-2 animate-in slide-in-from-top-1 fade-in duration-200"
			>
				<!-- ALL -->
				<button
					@click="setStatusFilter('all')"
					:class="[
        'flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-medium transition-all whitespace-nowrap',
        filterState.statuses.length === 0
          ? 'bg-slate-600 border-slate-600 text-white shadow-sm'
          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
      ]"
				>
					{{ $t('invoices.total_count') }}
					<span
						:class="[
          'px-2 py-0.5 rounded-full text-xs font-bold',
          filterState.statuses.length === 0
            ? 'bg-white/20 text-white'
            : 'bg-slate-100 text-slate-600'
        ]"
					>
        {{ invoiceStats.reduce((a, s) => a + s.count, 0) }}
      </span>
				</button>

				<!-- PER STATUS -->
				<button
					v-for="s in invoiceStats"
					:key="s.status"
					@click="setStatusFilter(s.status)"
					:class="[
        'flex items-center gap-2 px-4 py-1.5 rounded-full border text-sm font-medium transition-all whitespace-nowrap',
        filterState.statuses.includes(s.status)
          ? 'bg-slate-600 border-slate-600 text-white shadow-sm'
          : 'bg-white border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50'
      ]"
				>
					{{ $t(`invoices.status_${s.status}`) || s.status }}
					<span
						:class="[
          'px-2 py-0.5 rounded-full text-xs font-bold',
          filterState.statuses.includes(s.status)
            ? 'bg-white/20 text-white'
            : 'bg-slate-100 text-slate-600'
        ]"
					>
        {{ s.count }}
      </span>
				</button>
			</div>
		</div>

		<div
			v-if="activeFilterCount > 0"
			class="flex flex-wrap gap-2 items-center py-2 px-1"
		>
			<span class="text-xs font-semibold text-slate-500 uppercase mr-1">
				{{ $t('invoices.active_filters') || 'Active Filters:' }}
			</span>

			<!-- DATE -->
			<Badge
				v-if="filterState.dateFrom || filterState.dateTo"
				variant="outline"
				class="gap-1 bg-white pl-2 pr-1 py-1"
			>
				<span class="font-normal text-slate-500">
					{{ $t('invoices.filter_date_created') }}:
				</span>
						<span class="font-medium">
					{{ filterState.dateFrom || '...' }} – {{ filterState.dateTo || '...' }}
				</span>
				<button
					@click="clearFilter('dateFrom'); clearFilter('dateTo')"
					class="ml-1 p-0.5 hover:bg-slate-100 rounded-full"
				>
					<XIcon class="w-3 h-3 text-slate-400" />
				</button>
			</Badge>

			<!-- AMOUNT -->
			<Badge
				v-if="filterState.amountMin || filterState.amountMax"
				variant="outline"
				class="gap-1 bg-white pl-2 pr-1 py-1"
			>
				<span class="font-normal text-slate-500">
					{{ $t('invoices.filter_amount') }}:
				</span>
						<span class="font-medium">
					{{ filterState.amountMin || '0' }} – {{ filterState.amountMax || '∞' }}
				</span>
				<button
					@click="clearFilter('amountMin'); clearFilter('amountMax')"
					class="ml-1 p-0.5 hover:bg-slate-100 rounded-full"
				>
					<XIcon class="w-3 h-3 text-slate-400" />
				</button>
			</Badge>

			<Badge
					v-for="status in filterState.statuses"
					:key="status"
					variant="outline"
					class="gap-1 bg-white pl-2 pr-1 py-1"
				>
				<span class="font-normal text-slate-500">
					{{ $t('invoices.filter_status') }}:
				</span>
						<span class="font-medium">
					{{ $t(`invoices.status_${status}`) || status }}
				</span>
				<button
					@click="clearFilter('statuses', status)"
					class="ml-1 p-0.5 hover:bg-slate-100 rounded-full"
				>
					<XIcon class="w-3 h-3 text-slate-400" />
				</button>
			</Badge>

			<!-- TYPES -->
			<Badge
				v-for="type in filterState.types"
				:key="type"
				variant="outline"
				class="gap-1 bg-white pl-2 pr-1 py-1"
			>
				<span class="font-normal text-slate-500">
					{{ $t('invoices.filter_type') }}:
				</span>
				<span class="font-medium">
					{{ getInvoiceTypeLabel(type, t) }}
				</span>
				<button
					@click="clearFilter('types', type)"
					class="ml-1 p-0.5 hover:bg-slate-100 rounded-full"
				>
					<XIcon class="w-3 h-3 text-slate-400" />
				</button>
			</Badge>

				<!-- CLIENT -->
				<Badge
					v-if="filterState.clientName"
					variant="outline"
					class="gap-1 bg-white pl-2 pr-1 py-1"
				>
					<span class="font-normal text-slate-500">
						{{ $t('invoices.filter_client') }}:
					</span>
							<span class="font-medium">
						{{ filterState.clientName }}
					</span>
					<button
						@click="clearFilter('clientName')"
						class="ml-1 p-0.5 hover:bg-slate-100 rounded-full"
					>
						<XIcon class="w-3 h-3 text-slate-400" />
					</button>
				</Badge>

				<!-- PHONE -->
				<Badge
					v-if="filterState.phone"
					variant="outline"
					class="gap-1 bg-white pl-2 pr-1 py-1"
				>
				<span class="font-normal text-slate-500">
					{{ $t('invoices.filter_phone') }}:
				</span>
						<span class="font-medium">
					{{ filterState.phone }}
				</span>
				<button
					@click="clearFilter('phone')"
					class="ml-1 p-0.5 hover:bg-slate-100 rounded-full"
				>
					<XIcon class="w-3 h-3 text-slate-400" />
				</button>
			</Badge>

			<!-- RESET ALL -->
			<button
				@click="filterState = { ...initialFilterState }"
				class="text-xs text-slate-600 hover:text-slate-700 hover:underline ml-2 font-medium"
			>
				{{ $t('invoices.filter_reset') }}
			</button>
		</div>

		<!-- Top toolbar -->
		<div class="flex items-center justify-between text-sm text-slate-700 py-2 mb-3">
			<div class="flex items-center gap-6">
				<div>
					<span class="text-slate-500">Кількість:</span>
					<span class="ml-1 font-semibold text-slate-900">
				{{ toolbarCount }}
			</span>
				</div>

				<div>
					<span class="text-slate-500">Сума:</span>
					<span class="ml-1 font-semibold text-slate-900">
				{{ toolbarSumFormatted }}
			</span>
				</div>
			</div>

			<!-- RIGHT SIDE -->
			<div
				v-if="hasSelection"
				class="inline-flex items-center rounded-full
	       border border-slate-200 bg-slate-50
	       text-sm overflow-hidden"
			>
				<span class="px-4 py-1.5 text-slate-700 font-semibold">
					{{ $t('invoices.selected_count', { count: selectedIds.size }) }}
				</span>

				<span class="h-5 w-px bg-slate-200"></span>

				<button
					type="button"
					class="flex items-center gap-2 px-4 py-1.5
		       text-slate-600 hover:text-slate-800
		       cursor-pointer
		       disabled:opacity-40 disabled:cursor-not-allowed"
					:disabled="!hasCancelableSelection"
					@click="handleBulkCancelClick"
				>
					<BanIcon class="w-4 h-4" />
					{{ $t('invoices.cancel_selected') }}
				</button>
			</div>
		</div>

		<!-- Table Area -->
		<div class="min-h-[60vh] bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden flex flex-col relative z-0">
			<div
				v-if="isLoading"
				class="absolute inset-0 z-50 bg-white/70 backdrop-blur-[1px] flex flex-col items-center justify-center"
			>
				<div class="bg-white p-4 rounded-full shadow-xl border border-slate-100">
					<InvoiceFilterLoaderIcon class="w-16 h-16 text-slate-300" />
				</div>
			</div>

			<div class="overflow-auto">
				<div class="min-w-[1200px]">
				<table class="w-full text-sm text-left">
					<thead
						class="bg-slate-50/75 backdrop-blur text-slate-500 font-medium border-b border-slate-200 sticky top-0 z-10"
					>
					<tr>
						<th class="px-6 py-3 w-10">
							<Checkbox
								:checked="selectedIds.size === filteredInvoices.length && filteredInvoices.length > 0"
								@change="toggleAll"
							/>
						</th>
						<th class="px-6 py-3 cursor-pointer hover:bg-slate-100" @click="handleSort('date')">
							<div class="flex items-center gap-1">
								{{ $t('invoices.table_date') }}

								<ArrowUpIcon
									v-if="isSorted('date') && sortDirection('date') === 'asc'"
									class="w-3 h-3"
								/>

								<ArrowDownIcon
									v-if="isSorted('date') && sortDirection('date') === 'desc'"
									class="w-3 h-3"
								/>
							</div>
						</th>
						<th class="px-6 py-3 cursor-pointer hover:bg-slate-100" @click="handleSort('number')">
							<div class="flex items-center gap-1">
								{{ $t('invoices.invoice_number') }}

								<ArrowUpIcon
									v-if="isSorted('number') && sortDirection('number') === 'asc'"
									class="w-3 h-3"
								/>

								<ArrowDownIcon
									v-if="isSorted('number') && sortDirection('number') === 'desc'"
									class="w-3 h-3"
								/>
							</div>
						</th>
						<th
							class="px-6 py-3 cursor-pointer hover:bg-slate-100"
							@click="handleSort('recipientName')"
						>
							<div class="flex items-center gap-1">
								{{ $t('invoices.table_recipient_name') }}

								<ArrowUpIcon
									v-if="isSorted('recipientName') && sortDirection('recipientName') === 'asc'"
									class="w-3 h-3"
								/>

								<ArrowDownIcon
									v-if="isSorted('recipientName') && sortDirection('recipientName') === 'desc'"
									class="w-3 h-3"
								/>
							</div>
						</th>
						<th
							class="px-6 py-3 cursor-pointer hover:bg-slate-100"
							@click="handleSort('recipientContact')"
						>
							<div class="flex items-center gap-1">
								{{ $t('invoices.table_recipient_contact') }}

								<ArrowUpIcon
									v-if="isSorted('recipientContact') && sortDirection('recipientContact') === 'asc'"
									class="w-3 h-3"
								/>

								<ArrowDownIcon
									v-if="isSorted('recipientContact') && sortDirection('recipientContact') === 'desc'"
									class="w-3 h-3"
								/>
							</div>
						</th>
						<th
							class="px-6 py-3 text-center cursor-pointer hover:bg-slate-100"
							@click="handleSort('status')"
						>
							<div class="flex items-center justify-center gap-1">
								{{ $t('invoices.table_status') }}

								<ArrowUpIcon
									v-if="isSorted('status') && sortDirection('status') === 'asc'"
									class="w-3 h-3"
								/>

								<ArrowDownIcon
									v-if="isSorted('status') && sortDirection('status') === 'desc'"
									class="w-3 h-3"
								/>
							</div>
						</th>
						<th
							class="px-6 py-3 text-right cursor-pointer hover:bg-slate-100"
							@click="handleSort('totalAmount')"
						>
							<div class="flex items-center justify-end gap-1">
								{{ $t('invoices.table_amount') }}

								<ArrowUpIcon
									v-if="isSorted('totalAmount') && sortDirection('totalAmount') === 'asc'"
									class="w-3 h-3"
								/>

								<ArrowDownIcon
									v-if="isSorted('totalAmount') && sortDirection('totalAmount') === 'desc'"
									class="w-3 h-3"
								/>
							</div>
						</th>
						<th class="px-6 py-3 text-center">
							{{ $t('invoices.table_payment_link') }}
						</th>
						<th class="px-6 py-3 w-10" />
					</tr>
					</thead>
					<tbody class="divide-y divide-slate-100">
					<tr
						v-for="inv in paginatedInvoices"
						:key="inv.id"
						:class="`hover:bg-slate-50/80 transition-colors ${selectedIds.has(inv.id) ? 'bg-slate-50/30' : ''}`"
					>
						<td class="px-6 py-4">
							<Checkbox :checked="selectedIds.has(inv.id)" @change="toggleSelection(inv.id)" />
						</td>
						<td class="px-6 py-4 font-mono text-slate-600">
							{{ inv.date }}
						</td>
						<td class="px-6 py-4">
							<button
								@click="inv.status === InvoiceStatus.DRAFT ? emit('edit', inv) : emit('view', inv)"
								class="font-medium text-slate-600 hover:text-slate-800 hover:underline"
							>
								{{ inv.number }}
							</button>
						</td>
						<td class="px-6 py-4 text-slate-900 font-medium">
							{{ getRecipientName(inv) }}
						</td>
						<td class="px-6 py-4 text-slate-500 text-xs">
							{{ getRecipientContact(inv) }}
						</td>
						<td class="px-6 py-4 text-center">
							<Badge :variant="getStatusVariant(inv.status)">
								{{ $t(`invoices.status_${inv.status}`) || inv.status }}
							</Badge>
						</td>
						<td class="px-6 py-4 text-right font-bold text-slate-900">
							{{ (inv.totalAmount).toLocaleString('en-US', { minimumFractionDigits: 2 }) }}
							<span class="text-xs font-normal text-slate-400">{{ inv.currency }}</span>
						</td>
						<td class="px-6 py-4 text-center">
							<Button
								variant="ghost"
								size="icon"
								class="h-8 w-8 text-slate-600 hover:bg-slate-50"
								@click="openPaymentModal(inv)"
							>
								<LinkIcon class="w-4 h-4" />
							</Button>
						</td>
						<td class="px-6 py-4">
							<Dropdown align="right">
								<template #trigger>
									<Button
										variant="ghost"
										size="icon"
										class="h-8 w-8 text-slate-400 hover:text-slate-900"
									>
										<MoreHorizontalIcon class="w-4 h-4" />
									</Button>
								</template>

								<button
									@click="emit('edit', inv)"
									:disabled="!canEdit(inv)"
									class="block w-full px-4 py-2 text-left text-sm flex items-center gap-2
												 hover:bg-slate-100
												 disabled:opacity-40
												 disabled:cursor-not-allowed
												 disabled:hover:bg-transparent"
									>
									<EditIcon class="w-4 h-4" />
									{{ $t('invoices.action_edit') }}
								</button>

								<button
									@click="emit('duplicate', inv)"
									:disabled="!canDuplicate(inv)"
									class="block w-full px-4 py-2 text-left text-sm hover:bg-slate-100 flex items-center gap-2"
								>
									<CopyIcon class="w-4 h-4" />
									{{ $t('invoices.action_duplicate') }}
								</button>

								<div class="border-t border-slate-100 my-1" />

								<button
									@click="emit('cancel', inv)"
									:disabled="!canCancel(inv)"
									class="block w-full px-4 py-2 text-left text-sm flex items-center gap-2 text-red-600
												 hover:bg-red-50
												 disabled:opacity-40
												 disabled:cursor-not-allowed
												 disabled:hover:bg-transparent"
									>
									<BanIcon class="w-4 h-4" />
									{{ $t('invoices.action_cancel') }}
								</button>

								<button
									@click="inv.id && emit('delete', inv.id)"
									:disabled="!canDelete(inv)"
									class="block w-full px-4 py-2 text-left text-sm flex items-center gap-2
												 text-slate-400 hover:bg-red-50 hover:text-red-600
												 disabled:opacity-40
												 disabled:cursor-not-allowed
												 disabled:hover:bg-transparent
												 disabled:hover:text-slate-400"
									>
									<TrashIcon class="w-4 h-4" />
									{{ $t('invoices.action_delete') }}
								</button>
							</Dropdown>
						</td>
					</tr>
					</tbody>
				</table>
				</div>
			</div>

			<!-- Pagination -->
			<div
				class="border-t border-slate-200 px-6 py-4 flex items-center justify-between bg-slate-50/50"
			>
				<div class="flex items-center gap-4 text-sm text-slate-500">
          <span>
            {{ $t('invoices.pagination.showing') }}
            <span class="font-medium text-slate-900">
              {{
								sortedInvoices.length === 0
									? 0
									: (currentPage - 1) * itemsPerPage + 1
							}}
            </span>
            {{ $t('invoices.pagination.to') }}
            <span class="font-medium text-slate-900">
              {{
								Math.min(currentPage * itemsPerPage, sortedInvoices.length)
							}}
            </span>
            {{ $t('invoices.pagination.of') }}
            <span class="font-medium text-slate-900">
              {{ sortedInvoices.length }}
            </span>
          </span>
				</div>
				<div class="flex items-center gap-2">
					<Button
						variant="outline"
						size="sm"
						@click="currentPage = Math.max(1, currentPage - 1)"
						:disabled="currentPage === 1"
					>
						{{ $t('invoices.pagination.previous') }}
					</Button>
					<Button
						variant="outline"
						size="sm"
						@click="currentPage = Math.min(totalPages, currentPage + 1)"
						:disabled="currentPage === totalPages"
					>
						{{ $t('invoices.pagination.next') }}
					</Button>
				</div>
			</div>
		</div>

		<!-- Payment Modal -->
		<PaymentLinkModal
			v-if="isPaymentModalOpen"
			:isOpen="isPaymentModalOpen"
			:invoice="selectedInvoiceForPayment"
			@close="isPaymentModalOpen = false"
		/>

		<ExportModal
			:isOpen="isExportModalOpen"
			:totalFiltered="filteredInvoices.length"
			:totalSelected="selectedIds.size"
			@close="isExportModalOpen = false"
			@export="handleExport"
		/>

	</div>
</template>
